<script setup lang="ts">
import { ref, computed, watch } from 'vue';
const props = defineProps<{
  id: string;
  label: string;
  modelValue: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
}>();
const emit = defineEmits<{ 'update:modelValue': [value: string] }>();
const open = ref(false);
const trigger = ref<globalThis.HTMLButtonElement | null>(null);
const selected = computed(() => props.options.find((o) => o.value === props.modelValue)?.label);
watch(
  () => props.disabled,
  (value) => {
    if (value) open.value = false;
  },
);
function close(): void {
  open.value = false;
  trigger.value?.focus();
}
function choose(value: string): void {
  if (props.disabled || !props.options.some((o) => o.value === value)) return;
  emit('update:modelValue', value);
  close();
}
</script>
<template>
  <div class="immediate-choice" @keydown.esc.prevent.stop="close">
    <span :id="id + '-label'">{{ label }}</span>
    <button
      :id="id"
      ref="trigger"
      type="button"
      :disabled="disabled"
      :aria-expanded="open"
      :aria-controls="id + '-choices'"
      :aria-labelledby="id + '-label ' + id + '-selected'"
      @click="open = !open"
    >
      <span :id="id + '-selected'">{{ selected || placeholder || 'Choisir' }}</span
      ><span aria-hidden="true">{{ open ? '▴' : '▾' }}</span>
    </button>
    <ul v-if="open" :id="id + '-choices'" :aria-label="label">
      <li v-for="option in options" :key="option.value">
        <button
          type="button"
          :disabled="disabled"
          :aria-pressed="modelValue === option.value"
          @click="choose(option.value)"
        >
          {{ option.label }}
        </button>
      </li>
      <li v-if="!options.length">Aucun choix disponible.</li>
    </ul>
  </div>
</template>
<style scoped>
.immediate-choice {
  min-width: 0;
  position: relative;
}
button {
  width: 100%;
  min-height: 48px;
  padding: 12px;
  border: 1px solid #98a4b6;
  border-radius: 9px;
  background: white;
  color: #0b2341;
  font: inherit;
  text-align: left;
  overflow-wrap: anywhere;
}
button:disabled {
  opacity: 0.5;
}
.immediate-choice > button {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
}
ul {
  padding: 4px;
  margin: 4px 0 0;
  list-style: none;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #98a4b6;
  border-radius: 9px;
  background: white;
}
li button {
  border: 0;
}
button[aria-pressed='true'] {
  background: #edf0d6;
}
button:focus-visible {
  outline: 3px solid #536320;
  outline-offset: 2px;
}
</style>
